// Angular imports
import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot } from '@angular/router';

// 3rd party imports
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// Service imports
import { AuthService } from '../_services/auth.service';
import { UserService } from '../_services/data-services/user.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private userService: UserService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    const isLoggedIn = this.authService.isLoggedIn;

    if (isLoggedIn && !this.userService.getCurrentUserInfo()) {
      return this.userService.userPortalInfo().pipe(
        map((data) => {
          this.userService.setCurrentUserInfo(data);
          return this.processRouteActivation(isLoggedIn, route);
        }),
        catchError((error) => {
          return of(this.processRouteActivation(isLoggedIn, route));
        })
      );
    } else {
      return this.processRouteActivation(isLoggedIn, route);
    }
  }

  processRouteActivation(
    isLoggedIn: boolean,
    route: ActivatedRouteSnapshot
  ): boolean {
    const isLoginForm = route.routeConfig.path.indexOf('login') !== -1;

    if (isLoggedIn && isLoginForm) {
      this.router.navigate(['/']);
      return false;
    }

    if (!isLoggedIn && !isLoginForm) {
      let returnUrl = ((route as any)._routerState || {}).url;
      if (!returnUrl) {
        const paths = route.pathFromRoot
          .filter((p) => p.url.length > 0)
          .map((p) => p.url.map((us) => us.path).join('/'));
        if (paths.length > 0) {
          returnUrl += `/${paths.join('/')}`;
        }
        const queryParams = route.queryParamMap.keys.map(
          (key) => `${key}=${route.queryParamMap.get(key)}`
        );
        if (queryParams.length > 0) {
          returnUrl += `?${queryParams.join('&')}`;
        }
      }
      // Redirecting won't clear app cache in case of token expiry hence loggin out
      this.authService.logOut(returnUrl);
    }

    return isLoggedIn || isLoginForm;
  }
}
